/**
 * Auth Store — 管理后台登录态
 *
 * 状态:
 * - accessToken: 短期 token(2h)
 * - refreshToken: 长期 token(7d)— G6 阶段实现 refresh
 * - user: 当前账号信息(userType / associationId / companyId / talentId)
 *
 * 角色:5 类(PLATFORM_ADMIN / ASSOCIATION_PRIMARY / ASSOCIATION_SUB / COMPANY / TALENT)
 * 管理后台只支持前 3 类
 */

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { UserType } from '@nutrition-talent/shared-types';
import { post } from '@/api/client';

export const ADMIN_PAGE_PERMISSIONS = [
  'dashboard',
  'associations',
  'associationAccounts',
  'myAssociation',
  'subAccounts',
  'talents',
  'companies',
  'projects',
  'applications',
  'invitations',
  'dictionaries',
  'banners',
  'wechatUsers',
] as const;

export type AdminPagePermission = (typeof ADMIN_PAGE_PERMISSIONS)[number];

const ADMIN_PAGE_PERMISSION_SET = new Set<string>(ADMIN_PAGE_PERMISSIONS);
const STORAGE_KEY = 'nt_admin_auth';
const DEFAULT_ADMIN_ROUTE = '/admin/change-password';

const DEFAULT_PAGE_PERMISSIONS: Partial<Record<UserType, AdminPagePermission[]>> = {
  PLATFORM_ADMIN: [
    'dashboard',
    'associations',
    'associationAccounts',
    'talents',
    'companies',
    'projects',
    'applications',
    'invitations',
    'dictionaries',
    'banners',
    'wechatUsers',
  ],
  ASSOCIATION_PRIMARY: [
    'dashboard',
    'myAssociation',
    'subAccounts',
    'talents',
    'companies',
    'projects',
    
  ],
  ASSOCIATION_SUB: [
    'dashboard',
    'myAssociation',
    'talents',
    'companies',
    'projects',
    
  ],
};

export const PAGE_PERMISSION_LABELS: Record<AdminPagePermission, string> = {
  dashboard: '概览',
  associations: '学会管理',
  associationAccounts: '学会账号管理',
  myAssociation: '我的学会',
  subAccounts: '子账号管理',
  talents: '人才管理',
  companies: '企业管理',
  projects: '项目管理',
  applications: '报名管理',
  invitations: '邀约管理',
  dictionaries: '字典管理',
  banners: '轮播图',
  wechatUsers: '微信用户',
};

export const PAGE_PERMISSION_ROUTE_MAP: Partial<Record<AdminPagePermission, string>> = {
  dashboard: '/admin/dashboard',
  associations: '/admin/associations',
  associationAccounts: '/admin/sub-accounts',
  subAccounts: '/admin/sub-accounts',
  talents: '/admin/talents',
  companies: '/admin/companies',
  projects: '/admin/projects',
  applications: '/admin/applications',
  invitations: '/admin/invitations',
  dictionaries: '/admin/dictionaries',
  banners: '/admin/banners',
  wechatUsers: '/admin/wechat-users',
};

export interface AuthUser {
  id: string;
  username: string;
  userType: UserType;
  associationId?: string;
  associationName?: string;
  companyId?: string;
  talentId?: string;
  /** 是否主账号(学会子账号 = false) */
  isPrimary: boolean;
  displayName: string;
  pagePermissions: AdminPagePermission[];
}

interface PersistedAuth {
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
}

function isAdminPagePermission(value: unknown): value is AdminPagePermission {
  return typeof value === 'string' && ADMIN_PAGE_PERMISSION_SET.has(value);
}

function normalizePagePermissions(userType?: UserType | null, value?: unknown): AdminPagePermission[] {
  if (Array.isArray(value)) {
    const deduped = new Set<AdminPagePermission>();
    value.forEach((item) => {
      if (isAdminPagePermission(item)) {
        deduped.add(item);
      }
    });
    if (deduped.size > 0) {
      return [...deduped];
    }
  }

  return userType ? [...(DEFAULT_PAGE_PERMISSIONS[userType] ?? [])] : [];
}

function normalizeAuthUser(user: AuthUser): AuthUser {
  return {
    ...user,
    pagePermissions: normalizePagePermissions(user.userType, user.pagePermissions),
  };
}

export function resolveDefaultAdminPath(pagePermissions: AdminPagePermission[]): string {
  const routePriority: AdminPagePermission[] = [
    'dashboard',
    'associationAccounts',
    'subAccounts',
    'associations',
    'talents',
    'companies',
    'projects',
    'applications',
    'invitations',
    'dictionaries',
    'banners',
    
  ];
  const permissionSet = new Set(pagePermissions);
  const matchedPermission = routePriority.find((permission) => permissionSet.has(permission));
  return (matchedPermission && PAGE_PERMISSION_ROUTE_MAP[matchedPermission]) || DEFAULT_ADMIN_ROUTE;
}

function loadFromStorage(): PersistedAuth | null {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as PersistedAuth;
    if (!parsed?.user) {
      return null;
    }
    return {
      ...parsed,
      user: normalizeAuthUser(parsed.user),
    };
  } catch {
    return null;
  }
}

function saveToStorage(data: PersistedAuth | null): void {
  if (data) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
}

export const useAuthStore = defineStore('auth', () => {
  const persisted = loadFromStorage();
  const accessToken = ref<string | null>(persisted?.accessToken ?? null);
  const refreshToken = ref<string | null>(persisted?.refreshToken ?? null);
  const user = ref<AuthUser | null>(persisted?.user ?? null);

  const isLoggedIn = computed(() => !!accessToken.value && !!user.value);
  const userType = computed<UserType | null>(() => user.value?.userType ?? null);
  const pagePermissions = computed<AdminPagePermission[]>(() =>
    normalizePagePermissions(userType.value, user.value?.pagePermissions),
  );
  const pagePermissionSet = computed(() => new Set(pagePermissions.value));
  const defaultAdminPath = computed(() => resolveDefaultAdminPath(pagePermissions.value));

  /** 是否平台管理员 */
  const isPlatformAdmin = computed(() => userType.value === 'PLATFORM_ADMIN');
  /** 是否学会主账号 */
  const isAssociationPrimary = computed(() => userType.value === 'ASSOCIATION_PRIMARY');
  /** 是否学会子账号 */
  const isAssociationSub = computed(() => userType.value === 'ASSOCIATION_SUB');
  /** 任何学会账号(主/子) */
  const isAssociation = computed(
    () => userType.value === 'ASSOCIATION_PRIMARY' || userType.value === 'ASSOCIATION_SUB',
  );

  function hasPagePermission(permission: AdminPagePermission): boolean {
    return pagePermissionSet.value.has(permission);
  }

  function hasAnyPagePermission(permissions: AdminPagePermission[]): boolean {
    return permissions.some((permission) => hasPagePermission(permission));
  }

  /**
   * 登录(账号密码)— 平台/学会账号共用同一接口
   * 入参:username + password
   * 出参:accessToken + refreshToken + user
   */
  async function login(username: string, password: string): Promise<AuthUser> {
    // 后端登录响应是平铺结构:{accessToken, refreshToken, userId, username, userType, associationId, associationName, isPrimary, pagePermissions}
    const res = await post<{
      accessToken: string;
      refreshToken: string;
      userId: string;
      username: string;
      userType: UserType;
      associationId: string;
      associationName?: string;
      isPrimary: boolean;
      pagePermissions?: AdminPagePermission[];
    }>('/api/auth/admin/login', { account: username, password });

    const nextUser: AuthUser = {
      id: res.userId,
      username: res.username,
      userType: res.userType,
      associationId: res.associationId,
      associationName: res.associationName,
      isPrimary: res.isPrimary,
      displayName: res.username,
      pagePermissions: normalizePagePermissions(res.userType, res.pagePermissions),
    };

    accessToken.value = res.accessToken;
    refreshToken.value = res.refreshToken;
    user.value = nextUser;
    saveToStorage({
      accessToken: accessToken.value!,
      refreshToken: refreshToken.value!,
      user: user.value!,
    });
    return user.value!;
  }

  function logout(): void {
    accessToken.value = null;
    refreshToken.value = null;
    user.value = null;
    saveToStorage(null);
  }

  return {
    accessToken,
    refreshToken,
    user,
    isLoggedIn,
    userType,
    pagePermissions,
    defaultAdminPath,
    isPlatformAdmin,
    isAssociationPrimary,
    isAssociationSub,
    isAssociation,
    hasPagePermission,
    hasAnyPagePermission,
    login,
    logout,
  };
});
